// Todo: fix a11y issues

/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import { SimulatorNavigation, type SimulatorArea } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { AnimatePresence } from 'framer-motion'
import lodash from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getShellByElementId } from 'components/Editor/EditorContext/selectors/block'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsActiveNode } from 'services/Store/Project/selectors'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import Area from './Area'
import InputField from './InputField'
import InputResizerHandlers from './InputField/InputResizerHandlers'
import Item from './Item'
import Navigation from './Navigation/Navigation'
import ResizerHandlers from './ResizerHandlers'
import type { ILabeledGraphicProps } from './SimulatorContext'
import SimulatorContext from './SimulatorContext'
import * as s from './SimulatorElement.module.scss'
import SimulatorPdfView from './SimulatorPdfView'
import { getViewedCount, sanitizedValue } from './helper'
import type { SimulatorElementProps } from './simulatorTypes'
import { useFieldDrag } from './useFieldDrag'
import { useFieldValidation } from './useFieldValidation'
import { useBoxResize } from './useSelectAreaActions'
import { useSimulatorActions } from './useSimulatorActions'
import { useSimulatorSelection } from './useSimulatorSelection'

const SimulatorElement: SimulatorElementProps = ({
  block,
  element,
  styles,
  font,
  mode,
  state,
  waiting,
  setState,
  onChange,
}) => {
  const { value, id } = element
  const isEditor = mode.previewMode === PreviewMode.editor
  const isFill = isEditor && mode.editorMode === AppMode.fill
  const nodeId = block && getShellByElementId(block, id)
  const isActiveElement = useProjectContext(getIsActiveNode, nodeId || '')
  const [viewedItems, setViewedItems] = useState<Record<string, boolean>>(
    value.items.reduce((acc, item) => ({ ...acc, [item.value]: false }), {}),
  )

  const allViewed = lodash.every(viewedItems)
  const activeItemIndex = value.items.findIndex((item) => item.value === state?.active)
  const activeItem = value.items[activeItemIndex < 0 ? 0 : activeItemIndex]
  const prevActiveItemId = useRef(activeItem?.value)
  const headerId = `simulator-${id}`

  const imgElRef = useRef<HTMLImageElement | null>(null)
  const simulationContainerRef = useRef<HTMLElement | null>(null)

  const {
    selectedAreaId,
    selectedFieldId,
    showHighlight,
    handleSelectArea,
    handleSelectField,
    clearHighlight,
    onClickOutside,
    setSelectedFieldId,
  } = useSimulatorSelection({
    imgElRef,
    isEditor,
    activeState: state?.active,
  })

  const { invalidFields, onFieldValueChange, validateFieldOnBlur, validateFields } =
    useFieldValidation({ fields: activeItem?.fields, elementId: id })

  const initialAreas = useMemo(() => {
    return (activeItem ? activeItem?.areas : element.value.items[0].areas) || []
  }, [activeItem, element.value.items])

  const initialFields = useMemo(() => {
    return (activeItem ? activeItem?.fields : element.value.items[0]?.fields) || []
  }, [activeItem, element.value.items])

  useElementCompleted({ block, elementId: id, completed: allViewed, deviceMode: mode.deviceMode })

  const updateAreaInValue = useCallback(
    (area: SimulatorArea) => {
      const isNew = !initialAreas.some((a) => a.id === area.id)
      const updatedArea = isNew ? { ...area, showClickableArea: value.showArea } : area
      const updatedAreas = [...initialAreas.filter((a) => a.id !== area.id), updatedArea]
      const updatedItems = element.value.items.map((item) => ({
        ...item,
        areas: item.value === activeItem?.value ? updatedAreas : item.areas,
      }))
      onChange?.({ ...element.value, items: updatedItems })
    },
    [activeItem?.value, element.value, initialAreas, onChange, value.showArea],
  )

  const updateFieldInValue = useCallback(
    (field: {
      id: string
      x: number
      y: number
      width: number | string
      height: number | string
      value?: string
    }) => {
      const updatedFields = [...initialFields.filter((f) => f.id !== field.id), field]
      const updatedItems = element.value.items.map((item) => ({
        ...item,
        fields: item.value === activeItem?.value ? updatedFields : item.fields,
      }))
      onChange?.({ ...element.value, items: updatedItems })
    },
    [activeItem?.value, element.value, initialFields, onChange],
  )

  const {
    items: areas,
    onDrag,
    onDraw,
    onResize,
  } = useBoxResize({
    enabledDraw: isFill || false,
    boxNode: imgElRef.current,
    setActiveId: handleSelectArea,
    list: initialAreas,
    createOrUpdate: updateAreaInValue,
  })

  const {
    items: fields,
    onDrag: onFieldDrag,
    onResize: onFieldResize,
  } = useFieldDrag({
    enabled: isFill || false,
    boxNode: imgElRef.current,
    setActiveId: handleSelectField,
    list: initialFields,
    updateField: updateFieldInValue,
  })

  const selectedArea = areas.find((area) => area.id === selectedAreaId)

  const clearFieldSelection = useCallback(() => setSelectedFieldId(null), [setSelectedFieldId])

  const { onAddField, onAddNewItem, onLinkArea, onDeleteArea, onDeleteField } = useSimulatorActions(
    {
      activeItem,
      areas,
      fields,
      selectedArea,
      selectedAreaId,
      handleSelectArea,
      clearFieldSelection,
      state,
      value,
      onChange,
      updateAreaInValue,
    },
  )

  const sanitizeKey = element.value.items.map((item) => item.value).join(',')

  useEffect(() => {
    clearHighlight()
    if (activeItem && prevActiveItemId.current !== activeItem.value) {
      simulationContainerRef.current?.focus()
      prevActiveItemId.current = activeItem.value
    }

    if (activeItem && !viewedItems[activeItem.value]) {
      setViewedItems((prev) => ({ ...prev, [activeItem.value]: true }))
    }
  }, [activeItem, clearHighlight, viewedItems])

  useEffect(() => {
    const { hasChanges, update } = sanitizedValue(value)
    if (hasChanges) {
      onChange?.(update)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizeKey])

  useEffect(() => {
    setViewedItems((prev) => {
      const newViewed: Record<string, boolean> = {}

      value.items.forEach((item) => {
        newViewed[item.value] = prev[item.value] ?? false
      })

      return newViewed
    })
  }, [value.items])

  const contextValue = useMemo(
    (): ILabeledGraphicProps => ({
      styles,
      font,
      onLinkArea,
      mode,
      onAddNewItem,
      onAddField,
      onDeleteArea,
      onDeleteField,
      setState,
      showHighlight,
      value,
      onResize,
      onChange,
      waiting,
      elementId: id,
      state,
      validateFields,
      onFieldValueChange,
      validateFieldOnBlur,
      invalidFields,
    }),
    [
      styles,
      font,
      onLinkArea,
      mode,
      onAddNewItem,
      onAddField,
      onDeleteArea,
      onDeleteField,
      setState,
      showHighlight,
      value,
      onResize,
      onChange,
      waiting,
      id,
      state,
      validateFields,
      onFieldValueChange,
      validateFieldOnBlur,
      invalidFields,
    ],
  )

  if (mode.previewMode === PreviewMode.pdf) {
    return (
      <SimulatorPdfView
        contextValue={contextValue}
        element={element}
        selectedAreaId={selectedAreaId}
        styles={styles}
        viewedItems={viewedItems}
      />
    )
  }

  return (
    <>
      <ScreenReaderOnlyText as='h2' id={headerId}>
        {activeItem.label}
      </ScreenReaderOnlyText>
      <SimulatorContext.Provider value={contextValue}>
        <section
          aria-labelledby={headerId}
          className={s.root}
          ref={simulationContainerRef}
          tabIndex={-1}
        >
          <div
            className={cn(s.img, {
              [s.preview]: !isEditor,
              [s.disabled]: (!isFill || !isActiveElement) && isEditor,
            })}
            onClick={onClickOutside}
            onMouseDown={onDraw}
            ref={imgElRef}
            style={{ height: styles.shell?.minHeight || styles.shell?.height || 400 }}
          >
            <AnimatePresence>
              {activeItem && (
                <Item
                  image={activeItem.image}
                  key={activeItem.value}
                  overlay={activeItem.overlay}
                  showAnimation={!!(!isEditor && styles?.effects?.animation)}
                />
              )}
            </AnimatePresence>
            {!!areas.length &&
              areas.map((area: SimulatorArea) => (
                <Area
                  area={area}
                  container={simulationContainerRef.current}
                  currentItemId={activeItem.value || ''}
                  elementId={id}
                  key={area.id}
                  onMouseDown={(e) => onDrag(e, area)}
                  selected={selectedAreaId === area?.id}
                >
                  {isFill && <ResizerHandlers area={area} selected={selectedAreaId === area?.id} />}
                </Area>
              ))}
            {!!fields.length &&
              fields.map((field) => (
                <InputField
                  field={field}
                  key={field.id}
                  onMouseDown={(e) => onFieldDrag(e, field)}
                  onSelect={() => handleSelectField(field.id)}
                  onUpdateField={(values) => updateFieldInValue({ ...field, ...values })}
                  selected={selectedFieldId === field.id}
                >
                  {isFill && (
                    <InputResizerHandlers
                      field={field}
                      onResize={onFieldResize}
                      selected={selectedFieldId === field.id}
                    />
                  )}
                </InputField>
              ))}
          </div>

          {value.navigation !== SimulatorNavigation.NONE && (
            <Navigation viewed={getViewedCount(viewedItems)} />
          )}
        </section>
      </SimulatorContext.Provider>
    </>
  )
}
export default SimulatorElement
